import { useEffect, useRef, useState } from 'react';
import { Card, Tag } from 'antd';
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  CheckCircleOutlined,
  MinusCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useSimulation } from '../../context/SimulationContext';
import { Band } from '../../types/simulation.types';
import './FeedbackPanel.scss';

const bandConfig: Record<
  Band,
  { color: string; label: string; icon: React.ReactNode }
> = {
  low: {
    color: '#ff4d4f',
    label: 'Low',
    icon: <WarningOutlined />,
  },
  medium: {
    color: '#faad14',
    label: 'Medium',
    icon: <MinusCircleOutlined />,
  },
  high: {
    color: '#52c41a',
    label: 'High',
    icon: <CheckCircleOutlined />,
  },
};

const BAND_ORDINAL: Record<Band, number> = { low: 0, medium: 1, high: 2 };

type Trend = 'up' | 'down' | 'same';

const trendConfig: Record<Trend, { icon: React.ReactNode | null; className: string }> = {
  up:   { icon: <ArrowUpOutlined />,   className: 'trend-up' },
  down: { icon: <ArrowDownOutlined />, className: 'trend-down' },
  same: { icon: null,                  className: 'trend-same' },
};

function getTrend(current: Band, previous: Band | undefined): Trend | null {
  if (previous === undefined) return null;
  const delta = BAND_ORDINAL[current] - BAND_ORDINAL[previous];
  if (delta > 0) return 'up';
  if (delta < 0) return 'down';
  return 'same';
}

function formatDimensionName(dim: string): string {
  return dim.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function FeedbackPanel() {
  const { indicators, timeline } = useSimulation();
  const previousSnapshot = timeline.length > 1
    ? timeline[timeline.length - 2].indicatorSnapshot
    : undefined;
  const [pulsing, setPulsing] = useState(false);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (Object.keys(indicators).length === 0) return;
    setPulsing(true);
    const timer = setTimeout(() => setPulsing(false), 1800);
    return () => clearTimeout(timer);
  }, [indicators]);

  const entries = Object.entries(indicators);
  if (entries.length === 0) return null;

  return (
    <Card
      title="Status Indicators"
      className={`feedback-panel${pulsing ? ' feedback-panel--updated' : ''}`}
      size="small"
    >
      {entries.map(([dimension, band]) => {
        const config = bandConfig[band];
        const trend = getTrend(band, previousSnapshot?.[dimension]);
        return (
          <div key={dimension} className="indicator-row">
            <span className="indicator-label">
              {formatDimensionName(dimension)}
            </span>
            <div className="indicator-right">
              {trend !== null && (
                <span className={`trend-icon ${trendConfig[trend].className}`}>
                  {trendConfig[trend].icon}
                </span>
              )}
              <Tag color={config.color} icon={config.icon}>
                {config.label}
              </Tag>
            </div>
          </div>
        );
      })}
    </Card>
  );
}

export default FeedbackPanel;
